import * as React from 'react';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { SessionAudienceTypes } from 'config';
import moment from 'moment';
import eventLocationsValidation, {
  MULTI_DAY_EVENT_END_TIME,
  MULTI_DAY_EVENT_START_TIME,
} from '../../validation/event-locations-validation';
import {
  selectCourseOnlySessionMeta,
  selectLocations,
  selectLoader,
  selectSessionFee,
  selectSessionInformation,
} from '../../../../selector';
import {
  CourseOnlySessionStatus, IEventLocationsForm,
} from '../../../../interface';
import { createSessionActions } from '../../../../slice';
import EventLocationsForm from '../event-locations-form';

interface EventLocationsViewProps {
  editMode:boolean
}

export default function EventLocationsView({ editMode }: EventLocationsViewProps) {
  const dispatch = useDispatch();
  const loading = useSelector(selectLoader);
  const locations = useSelector(selectLocations);
  const { audienceType } = useSelector(selectSessionInformation);
  const { status } = useSelector(selectCourseOnlySessionMeta);
  const sessionFee = useSelector(selectSessionFee);

  const getDefaultLocationValues = React.useCallback((item: (typeof locations)[number]) => {
    const startDate = item.from ? moment(item.from).format('YYYY-MM-DD') : '';
    const endDate = item.to ? moment(item.to).format('YYYY-MM-DD') : '';
    const isMultiDayEvent = !!(
      startDate
      && endDate
      && !moment(startDate, 'YYYY-MM-DD', true).isSame(moment(endDate, 'YYYY-MM-DD', true), 'day')
    );

    return {
      startDate,
      endDate,
      from: isMultiDayEvent
        ? MULTI_DAY_EVENT_START_TIME
        : (item.from ? moment(item.from).format('HH:mm:ss') : ''),
      to: isMultiDayEvent
        ? MULTI_DAY_EVENT_END_TIME
        : (item.to ? moment(item.to).format('HH:mm:ss') : ''),
      address: item.address,
      description: item.description || '',
      isNewLocation: false,
    };
  }, [locations]);

  const methods = useForm<IEventLocationsForm>({
    defaultValues: {
      locations: locations.length > 0 ? locations.map((item) => getDefaultLocationValues(item)) : [{
        startDate: '',
        endDate: '',
        from: '',
        to: '',
        address: '',
        description: '',
        isNewLocation: true,
      }],
    },
    resolver: yupResolver(eventLocationsValidation),
    shouldFocusError: true,
  });

  React.useEffect(() => {
    methods.reset({
      locations: locations.length > 0 ? locations.map((item) => getDefaultLocationValues(item)) : [{
        startDate: '',
        endDate: '',
        from: '',
        to: '',
        address: '',
        description: '',
        isNewLocation: true,
      }],
    });
  }, [locations, methods, getDefaultLocationValues]);

  const onSubmit:
  SubmitHandler<IEventLocationsForm> = React.useCallback((data) => {
    const payload = data.locations.map((loc) => ({
      from: moment(`${loc.startDate} ${loc.from}`, 'YYYY-MM-DD HH:mm:ss').toISOString(),
      to: moment(`${loc.endDate} ${loc.to}`, 'YYYY-MM-DD HH:mm:ss').toISOString(),
      address: loc.address,
      description: loc.description,
    }));
    dispatch(createSessionActions.AddLocations(payload));
    if (audienceType === SessionAudienceTypes.courseOnly) {
      if (status === CourseOnlySessionStatus.editing || editMode) {
        dispatch(createSessionActions.editSession(sessionFee));
      } else {
        dispatch(createSessionActions.createSession(sessionFee));
      }
    }
  }, [dispatch, audienceType, status, editMode, sessionFee]);

  return (
    <div>
      <FormProvider {...methods}>
        <EventLocationsForm
          onSubmit={onSubmit}
          loading={loading}
          audienceType={audienceType}
          courseOnlyStatus={status}
          editMode={editMode}
        />
      </FormProvider>
    </div>
  );
}
